import {
  Badge,
  Box,
  Button,
  Flex,
  IconButton,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tooltip,
  Tr,
  useColorModeValue,
  useDisclosure,
} from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom';
import { MdAdd, MdDelete, MdRefresh, MdVideocam, MdVisibility } from 'react-icons/md';
import PageLayout from 'components/page-layout';
import PageCard from 'components/page-card';
import EmptyState from 'components/states/EmptyState';
import SkeletonTable from 'components/states/SkeletonTable';
import FadeIn from 'components/animations/FadeIn';
import { VideoIllustration } from 'components/illustrations';
import UploadRecordingModal from './components/UploadRecordingModal';
import { getProgressSummary } from './components/ProcessingTimeline';
import { CourtRecordingDto, CourtRecordingStatus } from '../../models/court-recording/dto/court-recording.dto';
import { useSelectedOrganization } from '../organization/hooks/useSelectedOrganization';
import ScopePrompt from '../_shared/ScopePrompt';
import { classifyRead } from '../_shared/readState';
import { useListCourtRecordings } from './hooks/useListCourtRecordings';
import { useRetryCourtRecording } from './hooks/useRetryCourtRecording';
import { useDeleteCourtRecording } from './hooks/useDeleteCourtRecording';
import { translate } from '../../i18n';

function formatDuration(ms: number): string {
  const seconds = Math.floor(ms / 1000);
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  return remainingMinutes > 0 ? `${hours}h ${remainingMinutes}m` : `${hours}h`;
}

const STATUS_COLORS: Record<CourtRecordingStatus, string> = {
  [CourtRecordingStatus.AWAITING_UPLOAD]: 'gray',
  [CourtRecordingStatus.QUEUED]: 'gray',
  [CourtRecordingStatus.PROCESSING]: 'brand',
  [CourtRecordingStatus.COMPLETED]: 'green',
  [CourtRecordingStatus.FAILED]: 'red',
};

export default function CourtRecordingPage() {
  const navigate = useNavigate();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const textColor = useColorModeValue('secondaryGray.900', 'white');
  const hoverBg = useColorModeValue('gray.50', 'whiteAlpha.100');

  const { selectedOrganization } = useSelectedOrganization();
  const orgId = selectedOrganization?.id || '';

  // `useListCourtRecordings` is `enabled: Boolean(organizationId)`, and React
  // Query v4 reports a DISABLED query as `isLoading: true` / `fetchStatus:
  // 'idle'` — so the raw flag this used to read drove the skeleton table
  // FOREVER for an operator with no organization selected. There is no scope
  // guard on this page to catch it. `classifyRead` takes the query's own
  // facts instead of a restatement of its gate (D62).
  const { data: recordings, isInitialLoading, isError } = useListCourtRecordings(orgId);
  const readState = classifyRead(isError, isInitialLoading, recordings);
  const retryMutation = useRetryCourtRecording(orgId);
  const deleteMutation = useDeleteCourtRecording(orgId);
  return (
    <PageLayout>
      <PageCard
        title={translate('court_recording.title')}
        rightHeader={
          <Button size="sm" variant="brand" leftIcon={<MdAdd />} onClick={onOpen}>
            {translate('court_recording.upload_video')}
          </Button>
        }
      >
        <Box px="25px" pb="20px">
          {readState === 'never-issued' ? (
            <ScopePrompt gate="organization" />
          ) : readState === 'loading' ? (
            <SkeletonTable />
          ) : readState === 'unreadable' ? (
            // `unreadable`, not the raw `isError`: a BACKGROUND refetch that
            // fails over rows already on screen must keep them rather than
            // replace the table with "failed to load" (R96-3).
            <EmptyState
              title={translate('court_recording.failed_to_load')}
              illustration={<VideoIllustration />}
            />
          ) : !recordings?.length ? (
            <EmptyState
              variant="first-use"
              illustration={<VideoIllustration />}
              title={translate('court_recording.empty_state')}
              actionLabel={translate('court_recording.upload_video')}
              onAction={onOpen}
            />
          ) : (
            <FadeIn>
              <Box overflowX="auto">
                <Table variant="simple" size="sm">
                  <Thead>
                    <Tr>
                      <Th>{translate('court_recording.court')}</Th>
                      <Th>{translate('court_recording.status')}</Th>
                      <Th>{translate('court_recording.created')}</Th>
                      <Th>{translate('court_recording.created_by')}</Th>
                      <Th>{translate('court_recording.actions')}</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {recordings.map((rec) => (
                      <Tr key={rec.id} _hover={{ bg: hoverBg }}>
                        <Td color={textColor}>{rec.courtName}</Td>
                        <Td>
                          <Badge colorScheme={STATUS_COLORS[rec.status]} variant="subtle">
                            {rec.status}
                          </Badge>
                          {rec.status === CourtRecordingStatus.FAILED && rec.errorMessage && (
                            <Tooltip label={rec.errorMessage}>
                              <Text as="span" fontSize="xs" color="red.400" ml={1} cursor="help">(?)</Text>
                            </Tooltip>
                          )}
                          {(() => {
                            const summary = getProgressSummary(rec);
                            return summary ? (
                              <Text fontSize="xs" color="brand.400" mt={1}>{summary}</Text>
                            ) : null;
                          })()}
                          <RecordingDiagnostics recording={rec} />
                        </Td>
                        <Td color={textColor} fontSize="sm">
                          {new Date(rec.createdAt).toLocaleDateString()} {new Date(rec.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        </Td>
                        <Td color={textColor} fontSize="sm">
                          {rec.createdBy ? (
                            rec.createdBy.email
                          ) : (
                            <Text as="span" color="gray.500" fontStyle="italic">
                              {translate('court_recording.auto_triggered')}
                            </Text>
                          )}
                        </Td>
                        <Td>
                          <Flex gap={1}>
                            <Tooltip label={translate('court_recording.view_analysis')}>
                              <IconButton
                                aria-label={translate('court_recording.view_analysis')}
                                icon={<MdVisibility />}
                                size="sm"
                                variant="ghost"
                                colorScheme="brand"
                                onClick={() => navigate(`/admin/court-recording/${rec.id}`)}
                              />
                            </Tooltip>
                            {(rec.status === CourtRecordingStatus.FAILED ||
                              (rec.status === CourtRecordingStatus.QUEUED && rec.lastTriggerError)) && (
                              <Tooltip label={translate('court_recording.retry_analysis')}>
                                <IconButton
                                  aria-label={translate('court_recording.retry_analysis')}
                                  icon={<MdRefresh />}
                                  size="sm"
                                  variant="ghost"
                                  colorScheme="brand"
                                  isLoading={retryMutation.isLoading}
                                  onClick={() => retryMutation.mutate(rec.id)}
                                />
                              </Tooltip>
                            )}
                            {rec.status !== CourtRecordingStatus.PROCESSING && (
                              <Tooltip label={translate('court_recording.delete')}>
                                <IconButton
                                  aria-label={translate('court_recording.delete')}
                                  icon={<MdDelete />}
                                  size="sm"
                                  variant="ghost"
                                  colorScheme="red"
                                  isLoading={deleteMutation.isLoading}
                                  onClick={() => {
                                    if (window.confirm(translate('court_recording.delete_confirm'))) {
                                      deleteMutation.mutate(rec.id);
                                    }
                                  }}
                                />
                              </Tooltip>
                            )}
                          </Flex>
                        </Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              </Box>
            </FadeIn>
          )}
        </Box>
      </PageCard>

      <UploadRecordingModal isOpen={isOpen} onClose={onClose} />
    </PageLayout>
  );
}

function RecordingDiagnostics({ recording }: { recording: CourtRecordingDto }) {
  const now = Date.now();

  if (recording.status === CourtRecordingStatus.QUEUED) {
    const queuedDuration = formatDuration(now - new Date(recording.createdAt).getTime());
    return (
      <Box mt={1}>
        <Text fontSize="xs" color="gray.500">
          {translate('court_recording.queued_ago').replace('{duration}', queuedDuration)}
        </Text>
        {recording.lastTriggerAttemptAt && (
          <Text fontSize="xs" color="gray.500">
            {translate('court_recording.last_trigger_attempt').replace(
              '{time}',
              formatDuration(now - new Date(recording.lastTriggerAttemptAt).getTime()),
            )}
          </Text>
        )}
        {recording.lastTriggerError && (
          <Tooltip label={recording.lastTriggerError}>
            <Text fontSize="xs" color="orange.400" cursor="help" noOfLines={1} maxW="200px">
              {recording.lastTriggerError}
            </Text>
          </Tooltip>
        )}
        {!recording.lastTriggerError && (
          <Text fontSize="xs" color="gray.400">
            {translate('court_recording.auto_retry_note')}
          </Text>
        )}
      </Box>
    );
  }

  if (recording.status === CourtRecordingStatus.PROCESSING && recording.processingStartedAt) {
    const processingDuration = formatDuration(now - new Date(recording.processingStartedAt).getTime());
    return (
      <Text fontSize="xs" color="brand.400" mt={1}>
        {translate('court_recording.processing_for').replace('{duration}', processingDuration)}
      </Text>
    );
  }

  return null;
}

